// src/App.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Layouts
import MainLayout from "./layouts/MainLayout";

// Pages
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import BoatShowPage from "./pages/BoatShowPage";
import ProspectsPage from "./pages/ProspectsPage";
import ModelsPage from "./pages/ModelsPage";
import ModelOptionsStaticPage from "./pages/ModelOptionsPage";
import UsersManagementPage from "./pages/UsersAccessStaticPage";
import WishListPage from "./pages/WishListPage";
import CalendarPage from "./pages/CalendarPage";
import BoatShowSchedulePage from "./pages/BoatShowSchedulePage";
import BoatShowAppointmentPage from "./pages/BoatShowAppointmentPage";
import WishlistManagementPage from "./pages/WishlistManagementPage";
import FournisseurPage from "./pages/FournisseurPage";
import NotFoundPage from "./pages/NotFoundPage";
import AccessDeniedPage from "./pages/AccessDeniedPage";
import ResetPassword from "./pages/ResetPassword";

// Création du thème
const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#dc004e",
    },
  },
});

// Fonction pour vérifier le rôle de l'utilisateur
const getUserRole = (): string | null => {
  return localStorage.getItem("role");
};

// Composant de protection des routes basé sur le rôle
interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
}) => {
  const userRole = getUserRole();

  if (!userRole) {
    // Si l'utilisateur n'est pas connecté, rediriger vers la page de connexion
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(userRole)) {
    // Si le rôle de l'utilisateur n'est pas autorisé, rediriger vers la page d'accès refusé
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          {/* Routes publiques */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Routes protégées */}
          <Route
            path="/"
            element={
              <ProtectedRoute allowedRoles={["admin", "agent"]}>
                <MainLayout>
                  <Home />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/boatshow"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <MainLayout>
                  <BoatShowPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/Prospects"
            element={
              <ProtectedRoute allowedRoles={["admin", "agent"]}>
                <MainLayout>
                  <ProspectsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/models"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <MainLayout>
                  <ModelsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/options"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <MainLayout>
                  <ModelOptionsStaticPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/access"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <MainLayout>
                  <UsersManagementPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/wishList"
            element={
              <ProtectedRoute allowedRoles={["admin", "agent"]}>
                <MainLayout>
                  <WishlistManagementPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/calendar"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <MainLayout>
                  <CalendarPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/boatshow-schedule"
            element={
              <ProtectedRoute allowedRoles={["admin", "agent"]}>
                <MainLayout>
                  <BoatShowSchedulePage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/boatshow-Appointment"
            element={
              <ProtectedRoute allowedRoles={["admin", "agent"]}>
                <MainLayout>
                  <BoatShowAppointmentPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/fournisseurs"
            element={
              <ProtectedRoute allowedRoles={["admin", "agent"]}>
                <MainLayout>
                  <FournisseurPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Routes d'erreur */}
          <Route path="/404" element={<NotFoundPage />} />
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
